import { useState, useCallback, useEffect } from 'react';
import { Typography, Divider, Fab, Button, Alert } from '@mui/material';
import Grid from '@mui/material/Grid';
import SearchIcon from '@mui/icons-material/Search';

import DocumentTypesField from './DocumentTypesField';
import { searchDocs } from '@/actions/search';
import SearchField from './SearchField';
import SearchResults from './SearchResults';
import { useApi } from '@/providers/ApiContext';
import { useDocTypes } from '@/providers/DocTypesContext';
import LoadingMask from '@/components/shared/LoadingMask';
import type { DocumentType } from '@/types/documentTypes';
import type { Doc } from '@/types/sharedTypes';
import type { EntityId } from '@/types/apiTypes';

interface SearchDocumentsProps {
  selectDocument: (doc_id: string, title: string) => void;
}

interface SearchResultDoc extends Doc {
  id: EntityId;
  doc_id?: string;
  title: string;
  [key: string]: unknown;
}

function SearchDocuments({ selectDocument }: SearchDocumentsProps): React.JSX.Element {
  const api = useApi();
  const doc_types = useDocTypes();
  const [search, setSearch] = useState<string>('');
  const [num_docs] = useState<number>(20);
  const [num_docs_error] = useState<boolean>(false);
  const [search_error, setSearchError] = useState<boolean | string>(false);
  const [search_results, setSearchResults] = useState<SearchResultDoc[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [documents_filter, setDocumentsFilter] = useState<DocumentType | null>(null);
  const [selected_document, setSelectedDocument] = useState<SearchResultDoc | null>(null);
  const [selection_doc_types, setSelectionDocTypes] = useState<DocumentType[] | null>(null);

  useEffect(() => {
    if (doc_types && !selection_doc_types) {
      // Filter out levels that are less than 2
      const selectable = doc_types.filter((doc_type) => doc_type.level >= 2);
      setDocumentsFilter(selectable[0] || null);
      setSelectionDocTypes(selectable);
    }
  }, [doc_types, selection_doc_types]);

  const handleSearchSubmit = useCallback(async () => {
    if (!api) return;
    setLoading(true);
    try {
      const searchResponse = await searchDocs(
        api,
        search,
        parseInt(num_docs.toString()),
        documents_filter?.entity_id ? [documents_filter.entity_id] : [],
      );
      const docs = (searchResponse as unknown as SearchResultDoc[]) || [];

      const unique_docs: SearchResultDoc[] = [];
      const doc_ids = new Set<string>();
      for (let i = 0; i < docs.length; i++) {
        const doc = docs[i] as SearchResultDoc;
        const docId = doc.doc_id || doc.id;
        if (docId && !doc_ids.has(docId)) {
          unique_docs.push(doc);
          doc_ids.add(docId);
        }
      }
      setSearchResults(unique_docs);
      setLoading(false);
    } catch (error) {
      console.log(error);
      setLoading(false);
    }
  }, [search, num_docs, documents_filter?.entity_id, api]);

  const handleDocSelection = useCallback(
    async (id: EntityId) => {
      const chunk = search_results.find((chunk) => chunk.id === id);
      setSelectedDocument(chunk || null);
    },
    [search_results],
  );

  const saveDocId = useCallback(async () => {
    if (selected_document && selected_document.doc_id && selected_document.title) {
      selectDocument(selected_document.doc_id, selected_document.title);
      window.scrollTo(0, 0);
    }
  }, [selected_document, selectDocument]);

  if (!selection_doc_types) {
    return <LoadingMask />;
  }

  return (
    <Grid container spacing={2}>
      <Grid size={12}>
        <Typography variant='h5'>Document Selection</Typography>
      </Grid>
      <Grid size={{ xs: 12, md: 6, lg: 4 }} sx={{ textAlign: 'left' }}>
        <DocumentTypesField
          options={selection_doc_types || []}
          value={documents_filter}
          setValue={(value) => setDocumentsFilter(value || null)}
        />
      </Grid>
      <Grid container size={12} paddingBottom={'15px'} spacing={1}>
        <Grid size={11}>
          <SearchField
            value={search}
            setValue={setSearch}
            error={search_error || num_docs_error}
            setError={setSearchError}
            onSubmit={handleSearchSubmit}
          />
        </Grid>
        <Grid size={1} sx={{ textAlign: 'right' }}>
          <Fab
            color='primary'
            aria-label='send'
            onClick={handleSearchSubmit}
            size='small'
            disabled={!documents_filter || loading || num_docs_error}
          >
            <SearchIcon />
          </Fab>
        </Grid>
      </Grid>
      {search_results.length ? (
        <>
          <Grid size={12}>
            <Divider />
          </Grid>
          <Grid size={12}>
            <Typography variant='h6'>Results</Typography>
          </Grid>
          <Grid size={12}>
            <SearchResults
              docs={search_results}
              setSelectedDocument={handleDocSelection}
            />
          </Grid>
          {selected_document ? (
            <>
              <Grid size={12}>
                <Divider />
              </Grid>
              <Grid size={12}>
                <Typography variant='h6'>
                  <b>{selected_document?.title}</b> selected for audit
                </Typography>
              </Grid>
              <Grid size={12}>
                <Button onClick={saveDocId} variant='contained'>
                  Confirm
                </Button>
              </Grid>
            </>
          ) : (
            <Grid size={12}>
              <Alert severity='info'>Select a document to audit</Alert>
            </Grid>
          )}
        </>
      ) : (
        <Grid size={12}>
          <Alert severity='info'>Search for a document to audit</Alert>
        </Grid>
      )}
    </Grid>
  );
}

export default SearchDocuments;

